/**
 * <gift-card-recipient>
 *
 * Gift-card recipient form for the product page. Paired with
 * snippets/gift-card-recipient-form.liquid and
 * assets/component-gift-card-recipient.css.
 *
 * Collapsed to a single checkbox until the buyer opts in; the fields then
 * expand and the values are exposed through getCartProperties() so
 * product-add-to-cart.js can submit them as line item properties.
 *
 * The property keys are the ones Shopify's checkout looks for, so they are the
 * literal English strings from the snippet's data-prop-key attributes and are
 * never localized.
 */
(function () {
  if (customElements.get('gift-card-recipient')) return;

  // Deliberately loose: the address is verified by Shopify on add/checkout. This
  // only catches obvious typos before the request goes out.
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  var CONTROL_PROP = '__shopify_send_gift_card_to_recipient';
  var OFFSET_PROP = '__shopify_offset';

  class GiftCardRecipient extends HTMLElement {
    connectedCallback() {
      // Guard against double init on Shopify section reloads.
      if (this._ready) return;
      this._ready = true;
      this._controller = new AbortController();

      this.refs = {};
      this.querySelectorAll('[data-ref]').forEach(function (el) {
        this.refs[el.getAttribute('data-ref')] = el;
      }, this);

      this._bindEvents();
      // The checkbox survives a soft reload (browsers restore its state), so sync
      // the fields to whatever it is now rather than assuming unchecked.
      this._onToggle();
    }

    disconnectedCallback() {
      this._ready = false;
      this._toggleExpressCheckout(true);
      if (this._controller) this._controller.abort();
    }

    _bindEvents() {
      var signal = this._controller.signal;
      var self = this;

      this.refs.checkbox.addEventListener('change', function () {
        self._onToggle();
      }, { signal: signal });

      // Clear a shown error as soon as the buyer starts fixing the address.
      this.refs.email.addEventListener('input', function () {
        self._clearError();
      }, { signal: signal });
    }

    _onToggle() {
      var on = this.refs.checkbox.checked;
      this.refs.fields.hidden = !on;
      this.classList.toggle('is-expanded', on);
      if (!on) this._clearError();
      // Express checkout submits its own form and never reads these fields, so it
      // would silently drop the recipient. Block it while the toggle is on.
      this._toggleExpressCheckout(!on);
      if (on) this.refs.email.focus();
    }

    _clearError() {
      this.classList.remove('is-invalid');
      if (this.refs.error) this.refs.error.textContent = '';
    }

    _productScope() {
      return this.closest('.product-details') || this.closest('section.product') || document;
    }

    /** @param {boolean} enabled */
    _toggleExpressCheckout(enabled) {
      var nodes = this._productScope().querySelectorAll('.hvg-express-checkout-buttons');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].classList.toggle('gift-card-recipient-disabled', !enabled);
      }
    }

    _value(ref) {
      return this.refs[ref] ? this.refs[ref].value.trim() : '';
    }

    /* ---- Public API for the add-to-cart hook ---- */

    /**
     * @returns {{valid: boolean, properties: Object}} Empty properties (and valid)
     * when the buyer has not opted in, so a plain gift card add is unaffected.
     */
    getCartProperties() {
      if (!this.refs.checkbox.checked) return { valid: true, properties: {} };

      var email = this._value('email');
      if (!EMAIL_PATTERN.test(email)) return { valid: false, properties: {} };

      var props = {};
      props[CONTROL_PROP] = 'on';
      props[this.refs.email.dataset.propKey] = email;
      // Lets Shopify resolve the scheduled send date in the buyer's timezone.
      props[OFFSET_PROP] = new Date().getTimezoneOffset().toString();

      ['name', 'sendOn', 'message'].forEach(function (ref) {
        var value = this._value(ref);
        if (value) props[this.refs[ref].dataset.propKey] = value;
      }, this);

      return { valid: true, properties: props };
    }

    /** Surface the validation error and move focus to the email field. */
    reportInvalid() {
      this.classList.add('is-invalid');
      if (this.refs.error) this.refs.error.textContent = this.dataset.invalidEmailMessage || '';
      this.refs.email.focus();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('gift-card-recipient', GiftCardRecipient);
})();
